import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { EmbeddedSetting } from 'src/app/core/state/_models/form.model';
import { Column as ReportColumn } from 'src/app/core/state/_models/report.models';
import { ChildTableColumn, ChildTableConfig, ChildTableRow, SelectOption, } from './models/child-table.models';
import { ChildTableComponent } from './child-table.component';
import { ImportResult } from './services/child-table-import.service';
import { ChildTableDataService, DropdownOption, PayloadColumn, SyncItem, } from './services/child-table-data.service';
import { evaluateRules, FieldStateMap, FormRule } from './core/form-rules';

/**
 * ChildTableEmbeddedComponent
 *
 * Thin wrapper that connects the form rendering context to the
 * presentation-only `app-child-table` component. It receives
 * EmbeddedSetting + parent context as inputs, uses ChildTableDataService
 * to load data and handle CRUD, and feeds everything into the child
 * table component via its standard inputs/outputs.
 *
 * Placement: inside ActionsComponent template when DbFieldType === 'CHILD_TABLE'
 * and the child table feature flag is ON. Originally designed to also replace
 * 'report' (embedded reports), later scoped to 'CHILD_TABLE' only.
 * The 'report' type stays on the old ngx-datatable.
 */
@Component({
  selector: 'app-child-table-embedded',
  template: `
    <div class="child-table-embedded-wrapper" *ngIf="config">
      <app-inline-banner *ngIf="error" type="error" [dismissable]="true" (dismissed)="error = null">
        {{ error }}
      </app-inline-banner>

      <app-child-table #childTableRef
        [config]="config"
        [data]="rows"
        [totalRecords]="totalRecords"
        [title]="displayTitle"
        [tableTitle]="displayTitle"
        [tableKey]="tableKey"
        [showAutoSaveStatus]="!!parentRecordId"
        [reportUrl]="!editable ? reportLinkUrl : ''"
        (cellChange)="onCellChange($event)"
        (rowAdd)="onRowAdd($event)"
        (rowDelete)="onRowDelete($event)"
        (save)="onSave($event)"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)"
        (sortChange)="onSortChange($event)"
        (searchChange)="onSearchChange($event)"
        (importComplete)="onImportComplete($event)"
      ></app-child-table>
    </div>

    <div *ngIf="loading && !config" class="ct-sk" role="region"
         [attr.aria-label]="(displayTitle || 'Table') + ' loading'" aria-busy="true">

      <!-- Title zone — mirrors real .table-header padding/border -->
      <div class="ct-sk__head">
        <div class="ct-sk-b ct-sk-b--label"></div>
        <div class="ct-sk-b ct-sk-b--title"></div>
      </div>

      <!-- Column header row — mirrors real grid-header height 50px -->
      <div class="ct-sk__col-header">
        <div class="ct-sk-b ct-sk-b--chk"></div>
        <div class="ct-sk-b ct-sk-b--num"></div>
        <div class="ct-sk-b ct-sk-b--c1"></div>
        <div class="ct-sk-b ct-sk-b--c2"></div>
        <div class="ct-sk-b ct-sk-b--c3"></div>
        <div class="ct-sk-b ct-sk-b--c4"></div>
      </div>

      <!-- Body rows — mirrors real grid-body row height 44px -->
      <div class="ct-sk__body">
        <div class="ct-sk__row" *ngFor="let r of [1,2,3,4,5]">
          <div class="ct-sk-b ct-sk-b--chk ct-sk-b--dim"></div>
          <div class="ct-sk-b ct-sk-b--num ct-sk-b--dim"></div>
          <div class="ct-sk-b ct-sk-b--d1"></div>
          <div class="ct-sk-b ct-sk-b--d2"></div>
          <div class="ct-sk-b ct-sk-b--d3"></div>
          <div class="ct-sk-b ct-sk-b--d4"></div>
        </div>
      </div>

      <span class="ct-sk__sr" aria-live="polite">Loading records</span>
    </div>
  `,
  styles: [`
    .child-table-embedded-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--og-spacing-sm, 8px);
      margin: 8px 0;
    }
    app-inline-banner {
      display: block;
    }

    /* ─── Skeleton loader ──────────────────────────────────────────────────── */
    /*
     * Technique: ONE wave sweeps the entire card via ::after.
     * Bones are flat — no internal gradients or per-bone animation.
     * All dimensions mirror the real child-table component exactly.
     */

    .ct-sk {
      position: relative;
      margin: 8px 0;
      border: 1px solid rgba(0, 0, 0, 0.09);
      border-radius: 8px;         /* matches .child-table-container */
      background: #ffffff;
      overflow: hidden;
    }

    /* Single wave that sweeps through the whole surface — the only animation */
    .ct-sk::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: linear-gradient(
        108deg,
        transparent 30%,
        rgba(255, 255, 255, 0.72) 50%,
        transparent 70%
      );
      transform: translateX(-110%);
      animation: ct-wave 2.2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    /* ── Bones — flat rects, no animation ─────────────────────── */
    .ct-sk-b {
      background: #ebebeb;
      border-radius: 4px;
      flex-shrink: 0;
    }
    .ct-sk-b--dim { opacity: 0.55; }

    /* ── Title zone — mirrors .table-header ───────────────────── */
    .ct-sk__head {
      display: flex;
      flex-direction: column;
      gap: 9px;
      padding: 16px 20px;                /* matches padding: 1rem 1.25rem */
      border-bottom: 1px solid rgba(0, 0, 0, 0.07);
    }
    .ct-sk-b--label { width: 72px;  height: 10px; border-radius: 3px; background: #f2f2f2; }
    .ct-sk-b--title { width: 190px; height: 18px; }

    /* ── Column header — mirrors .grid-header height: 50px ─────── */
    .ct-sk__col-header {
      display: grid;
      grid-template-columns: 48px 44px minmax(120px, 1.8fr) minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(64px, 0.7fr);
      align-items: center;
      height: 50px;
      padding: 0 20px 0 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.07);
      background: #fafafa;
    }
    .ct-sk__col-header .ct-sk-b { margin: 0 16px; background: #e2e2e2; }

    /* ── Body rows — mirrors .grid-body row height: 44px ──────── */
    .ct-sk__body {
      padding-bottom: 2px;
    }
    .ct-sk__row {
      display: grid;
      grid-template-columns: 48px 44px minmax(120px, 1.8fr) minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(64px, 0.7fr);
      align-items: center;
      height: 44px;
      padding: 0 20px 0 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.045);
    }
    .ct-sk__row:last-child { border-bottom: none; }
    .ct-sk__row .ct-sk-b { margin: 0 16px; }

    /* Checkbox + row-number columns */
    .ct-sk-b--chk { width: 14px; height: 14px; border-radius: 3px; justify-self: center; margin: 0 auto; }
    .ct-sk-b--num { width: 22px; height: 12px; justify-self: center; margin: 0 auto; }

    /* Column header label widths */
    .ct-sk__col-header .ct-sk-b--c1 { height: 11px; width: 68%; }
    .ct-sk__col-header .ct-sk-b--c2 { height: 11px; width: 54%; }
    .ct-sk__col-header .ct-sk-b--c3 { height: 11px; width: 62%; }
    .ct-sk__col-header .ct-sk-b--c4 { height: 11px; width: 50%; }

    /* Row 1 */
    .ct-sk__row:nth-child(1) .ct-sk-b--d1 { height: 13px; width: 82%; }
    .ct-sk__row:nth-child(1) .ct-sk-b--d2 { height: 13px; width: 65%; }
    .ct-sk__row:nth-child(1) .ct-sk-b--d3 { height: 13px; width: 50%; }
    .ct-sk__row:nth-child(1) .ct-sk-b--d4 { height: 13px; width: 36px; }
    /* Row 2 */
    .ct-sk__row:nth-child(2) .ct-sk-b--d1 { height: 13px; width: 67%; }
    .ct-sk__row:nth-child(2) .ct-sk-b--d2 { height: 13px; width: 78%; }
    .ct-sk__row:nth-child(2) .ct-sk-b--d3 { height: 13px; width: 43%; }
    .ct-sk__row:nth-child(2) .ct-sk-b--d4 { height: 13px; width: 28px; }
    /* Row 3 */
    .ct-sk__row:nth-child(3) .ct-sk-b--d1 { height: 13px; width: 90%; }
    .ct-sk__row:nth-child(3) .ct-sk-b--d2 { height: 13px; width: 56%; }
    .ct-sk__row:nth-child(3) .ct-sk-b--d3 { height: 13px; width: 62%; }
    .ct-sk__row:nth-child(3) .ct-sk-b--d4 { height: 13px; width: 32px; }
    /* Row 4 */
    .ct-sk__row:nth-child(4) .ct-sk-b--d1 { height: 13px; width: 73%; }
    .ct-sk__row:nth-child(4) .ct-sk-b--d2 { height: 13px; width: 48%; }
    .ct-sk__row:nth-child(4) .ct-sk-b--d3 { height: 13px; width: 55%; }
    .ct-sk__row:nth-child(4) .ct-sk-b--d4 { height: 13px; width: 24px; }
    /* Row 5 */
    .ct-sk__row:nth-child(5) .ct-sk-b--d1 { height: 13px; width: 58%; }
    .ct-sk__row:nth-child(5) .ct-sk-b--d2 { height: 13px; width: 71%; }
    .ct-sk__row:nth-child(5) .ct-sk-b--d3 { height: 13px; width: 46%; }
    .ct-sk__row:nth-child(5) .ct-sk-b--d4 { height: 13px; width: 30px; }

    .ct-sk__sr {
      position: absolute;
      width: 1px; height: 1px;
      padding: 0; margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    @keyframes ct-wave {
      to { transform: translateX(120%); }
    }

    @media (max-width: 860px) {
      .ct-sk__col-header,
      .ct-sk__row {
        grid-template-columns: 40px 36px minmax(90px, 1.8fr) minmax(80px, 1.2fr) minmax(64px, 1fr) minmax(48px, 0.6fr);
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildTableEmbeddedComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('childTableRef') childTableRef?: ChildTableComponent;

  /** EmbeddedSetting from the form field definition. */
  @Input() embeddedSetting?: EmbeddedSetting;

  /** Child table key (from the form field's ApplicationTableKey or relation). */
  @Input() tableKey = '';

  /** Display title for the table. */
  @Input() tableTitle = '';

  /** Report key defining visible columns (from EmbeddedSetting.MatchRecordsReportKey or DefaultValue). */
  @Input() reportKey = '';

  /** Parent record ID -- filters child rows to this parent. */
  @Input() parentRecordId = '';

  /** Reference field key in the child table that links to the parent. */
  @Input() referenceFieldKey = '';

  /** Parent table key -- used to discover the relation if referenceFieldKey is not provided. */
  @Input() parentTableKey = '';

  /** Application key (for CRUD payloads). */
  @Input() applicationKey = '';

  /** Whether inline editing is allowed. */
  @Input() editable = true;

  /** Emits true when the table has validation errors, false otherwise. */
  @Output() validationStatusChange = new EventEmitter<boolean>();
  private currentValidationStatus = false;

  // --- Component state ---
  config: ChildTableConfig<any> | null = null;
  rows: any[] = [];
  totalRecords = 0;
  displayTitle = '';
  loading = false;
  error: string | null = null;

  get reportLinkUrl(): string {
    if (!this.applicationKey || !this.tableKey || !this.reportKey) return '';
    return `/apps/${this.applicationKey}/tables/${this.tableKey}/reports/datatable/view/${this.reportKey}`;
  }

  // Pagination/sort state
  private currentPage = 0;
  private pageSize = 5;
  private draw = 0;
  private sortColumn = -1;
  private sortDir: 'asc' | 'desc' = 'asc';
  private searchValue = '';

  // Field metadata for building payloads
  private fields: ReportColumn[] = [];
  private payloadColumns: PayloadColumn[] = [];
  private dataKeyMap: Record<string, string> = {};
  /** Maps data column key (used in row.data) back to the field's Key identifier. */
  private columnKeyToFieldKey: Record<string, string> = {};
  /** Maps data column key (used in row.data) back to the field's FieldName (database column name for CRUD). */
  private columnKeyToFieldName: Record<string, string> = {};
  /** Maps data column key to the backend field type string (e.g. "VARCHAR", "INT"). */
  private columnKeyToFieldType: Record<string, string> = {};
  /** Cached user options for USER-type columns (fetched once during init). */
  private userOptions: SelectOption[] = [];
  /** The FieldName (database column name) for the reference/FK field. */
  private referenceFieldName = '';
  /** Prevents duplicate syncs while one is in flight */
  private syncInFlight = false;
  /** Rows queued while a sync was in flight — drained when the current sync finishes. */
  private syncQueue: ChildTableRow<any>[] = [];
  /** Row IDs that couldn't sync because parentRecordId was missing (new-record flow) */
  private readonly pendingRowIds = new Set<string>();
  private hasUnsavedImportedRows = false;
  
  /** Form rules and static settings fetched from the linked form */
  private formRules: FormRule[] = [];
  private formSavedFields: any = null;
  /** Map of rowId -> FieldStateMap containing dynamic rule evaluations */
  private readonly rowFieldStates = new Map<string, FieldStateMap>();
  
  /** Tracks if we've already shown the validation alert to avoid spamming the user */
  private hasShownValidationAlert = false;

  /** Row IDs blocked by validation that need to be re-synced once errors are resolved */
  private readonly validationBlockedRowIds = new Set<string>();

  private static readonly SYSTEM_FIELDS = new Set([
    'createdby', 'modifiedby', 'datecreated', 'datemodified', 'id',
  ]);

  private static stripTablePrefix(name: string): string {
    const match = /^tbl\d+_(.+)$/.exec(name);
    return match ? match[1] : name;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dataService: ChildTableDataService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeAndLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When parentRecordId transitions from falsy to truthy, re-enable server-side
    // pagination (was disabled because no parent existed) and re-sync pending rows.
    if (changes['parentRecordId'] && !changes['parentRecordId'].firstChange) {
      const prev = changes['parentRecordId'].previousValue;
      const curr = changes['parentRecordId'].currentValue;
      if (!prev && curr) {
        if (this.config && !this.hasUnsavedImportedRows) {
          this.config.serverSidePagination = true;
        }
        if (this.pendingRowIds.size > 0) {
          this.resyncPendingRows();
        }
      }
    }

    if (changes['tableKey'] || changes['reportKey'] || changes['parentRecordId'] || changes['parentTableKey']) {
      if ((this.tableKey || this.parentTableKey) && !changes['tableKey']?.firstChange) {
        this.initializeAndLoad();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.currentValidationStatus) {
      this.validationStatusChange.emit(false);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -------------------------------------------------------------------
  // Initialization
  // -------------------------------------------------------------------

  private initializeAndLoad(): void {
    this.displayTitle = this.embeddedSetting?.AllowDisplayName && this.embeddedSetting?.DisplayName
      ? this.embeddedSetting.DisplayName
      : '';

    // Guard: if tableKey was stored incorrectly as the parent table key (happens when the form
    // was saved before ApplicationTableKey was populated by the form builder), clear it so the
    // relation-discovery path below can find the correct child table key.
    if (this.tableKey && this.parentTableKey && this.tableKey === this.parentTableKey) {
      this.tableKey = '';
    }

    // If no tableKey provided (CHILD_TABLE design element), auto-discover the child table.
    // When a reportKey exists, resolve the table directly from the report columns — this
    // avoids the ambiguity of picking the first "Has many" relation when multiple child
    // tables are linked to the same parent.
    if (!this.tableKey && this.parentTableKey) {
      this.loading = true;
      this.cdr.markForCheck();

      if (this.reportKey) {
        this.resolveTableFromReport();
        return;
      }

      this.discoverTableFromRelations();
      return;
    }

    if (!this.tableKey) return;
    this.continueInitialization();
  }

  /**
   * Resolve the child table key from the report columns API.
   * The report knows which table it belongs to, so its columns carry the correct
   * ApplicationTableKey. After resolving, find the matching relation for FK info.
   */
  private resolveTableFromReport(): void {
    this.dataService.getReportColumns(this.reportKey, this.parentTableKey).pipe(
      takeUntil(this.destroy$),
      map(cols => cols?.[0]?.ApplicationTableKey ?? ''),
    ).subscribe({
      next: (resolvedTableKey) => {
        if (resolvedTableKey && resolvedTableKey !== this.parentTableKey) {
          this.tableKey = resolvedTableKey;
          this.dataService.findChildRelation(this.parentTableKey, this.tableKey).pipe(
            takeUntil(this.destroy$),
          ).subscribe({
            next: (rel) => {
              if (rel) {
                this.referenceFieldKey = rel.ReferenceFieldKey ?? rel.LookupFieldKey ?? '';
                if (rel.ReferenceFieldName) this.referenceFieldName = rel.ReferenceFieldName;
              }
              this.continueInitialization();
            },
            error: () => this.continueInitialization(),
          });
        } else {
          this.discoverTableFromRelations();
        }
      },
      error: () => this.discoverTableFromRelations(),
    });
  }

  /**
   * Fallback: discover the child table by finding the first "Has many" relation
   * from the parent table. Works when there is only one child table or no reportKey.
   */
  private discoverTableFromRelations(): void {
    this.dataService.getRelations(this.parentTableKey).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (relations) => {
        const hasManyRelation = relations.find(r => {
          if (r.RelationType !== 'Has many') return false;
          return r.ApplicationTable2Key === this.parentTableKey;
        });

        if (hasManyRelation) {
          const childKey = hasManyRelation.ApplicationTableKey;
          const childName = hasManyRelation.ApplicationName2;

          this.tableKey = childKey;
          this.referenceFieldKey = hasManyRelation.ReferenceFieldKey ?? hasManyRelation.LookupFieldKey ?? '';
          if (hasManyRelation.ReferenceFieldName) {
            this.referenceFieldName = hasManyRelation.ReferenceFieldName;
          }
          if (!this.displayTitle) {
            this.displayTitle = childName || '';
          }
          this.continueInitialization();
        } else {
          this.loading = false;
          this.error = 'No child table relationship found.';
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.loading = false;
        this.error = 'Failed to discover child table.';
        this.cdr.markForCheck();
      },
    });
  }

  private continueInitialization(): void {

    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    // Discover relation in parallel (independent of column fetching)
    if (!this.referenceFieldKey && this.parentTableKey) {
      this.dataService.findChildRelation(this.parentTableKey, this.tableKey).pipe(
        takeUntil(this.destroy$),
      ).subscribe({
        next: (rel) => {
          if (rel) {
            this.referenceFieldKey = rel.ReferenceFieldKey;
            if (rel.ReferenceFieldName) {
              this.referenceFieldName = rel.ReferenceFieldName;
            }
          }
        },
      });
    }

    // Step 1: Resolve report key (discover default if not provided)
    const reportKey$ = this.reportKey
      ? of(this.reportKey)
      : this.dataService.getReports(this.tableKey).pipe(
          switchMap((reports) => {
            const defaultReport = reports.find((r: any) => r.DefaultReport === 1) ?? reports[0];
            if (defaultReport) {
              this.reportKey = defaultReport.Key;
              return of(this.reportKey);
            }
            return of('');
          }),
          catchError(() => of('')),
        );

    // Step 2: Fetch report details (to get FormKey), report columns, and all table fields in parallel.
    reportKey$.pipe(
      switchMap((reportKey) => {
        const allFields$ = (this.referenceFieldKey || this.parentTableKey)
          ? this.dataService.getTableFields(this.tableKey).pipe(
              catchError(() => of([] as ReportColumn[])),
              shareReplay(1),
            )
          : of([] as ReportColumn[]);

        const tableFieldsFallback$ = (this.referenceFieldKey || this.parentTableKey)
          ? allFields$
          : this.dataService.getTableFields(this.tableKey).pipe(catchError(() => of([] as ReportColumn[])));

        const reportCols$ = reportKey
          ? this.dataService.getReportColumns(reportKey, this.tableKey).pipe(
              switchMap((cols) => cols && cols.length > 0 ? of(cols) : tableFieldsFallback$),
              catchError(() => tableFieldsFallback$),
            )
          : tableFieldsFallback$;

        const reportDetails$ = reportKey
          ? this.dataService.getReportById(reportKey, this.tableKey)
          : of(null);

        return forkJoin({ reportCols: reportCols$, allFields: allFields$, reportDetails: reportDetails$ });
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ reportCols, allFields, reportDetails }) => {
        // Resolve FK field name from the full table fields (which include the FK column)
        if (this.referenceFieldKey) {
          const refFromAll = allFields.find(f => f.Key === this.referenceFieldKey);
          if (refFromAll) {
            this.referenceFieldName = refFromAll.FieldName;
            this.referenceFieldKey = refFromAll.Key;
          }
          if (!this.referenceFieldName) {
            const refFromReport = reportCols.find(f => f.Key === this.referenceFieldKey);
            if (refFromReport) {
              this.referenceFieldName = refFromReport.FieldName;
            }
          }
        }

        // Fallback: if referenceFieldKey didn't match any field (e.g. LookupFieldKey refers to
        // the parent side), find the FK field by IsRelation flag in the child table's fields
        if (!this.referenceFieldName && allFields.length > 0) {
          const relationField = allFields.find(f => f.IsRelation === true || f.FieldType === 'Relationship');
          if (relationField) {
            this.referenceFieldName = relationField.FieldName;
            this.referenceFieldKey = relationField.Key;
          }
        }

        console.log('[ChildTable] FK resolved:', { referenceFieldKey: this.referenceFieldKey, referenceFieldName: this.referenceFieldName, allFieldsCount: allFields.length, reportColsCount: reportCols.length });

        // Filter out relation/FK columns so they don't appear in the UI
        const displayFields = reportCols.filter(f => {
          if (f.IsRelation) return false;
          if (f.FieldType === 'Relationship') return false;
          if (f.Key === this.referenceFieldKey) return false;
          return true;
        });

        this.fields = displayFields;
        this.payloadColumns = this.dataService.buildPayloadColumns(displayFields);
        
        if (!reportDetails && this.reportKey) {
          // Report was deleted or is invalid. Clear it so data loading doesn't fail.
          this.reportKey = '';
        }

        const linkedFormKey = this.embeddedSetting?.MatchRecordsRelatedFormKey ?? reportDetails?.FormKey;
        this.fetchOptionsAndLoad(displayFields, linkedFormKey);
      },
      error: () => {
        this.loading = false;
        this.error = 'Failed to load table fields.';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Fetch dropdown values for ENUM/ENUM2 fields and user options for USER
   * fields in parallel, then merge them into field metadata before the
   * first data load. Only called once during initialization.
   */
  private fetchOptionsAndLoad(fields: ReportColumn[], linkedFormKey?: string): void {
    const enumFieldKeys = fields
      .filter(f => {
        const ft = f.FieldType?.toUpperCase() ?? '';
        return (ft === 'ENUM' || ft === 'ENUM2') && (!f.Values || f.Values.length === 0);
      })
      .map(f => f.Key);

    const hasUserFields = fields.some(f => f.FieldType?.toUpperCase() === 'USER');
    const appKey = this.applicationKey;

    forkJoin({
      dropdowns: enumFieldKeys.length > 0 ? this.dataService.fetchAllDropdownValues(enumFieldKeys) : of({} as Record<string, DropdownOption[]>),
      users: hasUserFields && appKey
        ? this.dataService.fetchUsers(this.tableKey, appKey)
        : of([] as DropdownOption[]),
      savedFields: linkedFormKey
        ? this.dataService.fetchFormSavedFields(linkedFormKey, this.tableKey, this.parentTableKey)
        : of(null),
      rules: linkedFormKey
        ? this.dataService.fetchFormRules(linkedFormKey, this.tableKey)
        : of([]),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ dropdowns, users, savedFields, rules }) => {
        for (const field of this.fields) {
          if (dropdowns[field.Key]?.length) {
            field.Values = dropdowns[field.Key]
              .filter(d => d.IsActive !== false)
              .map(d => ({ Value: d.Value ?? d.Name ?? '' }));
          }
        }
        if (users.length > 0) {
          this.userOptions = users
            .filter(u => u.Value)
            .map(u => ({ value: u.Value!, label: u.Name ?? u.Value! }));
        }
        
        this.formSavedFields = savedFields;
        this.formRules = rules ?? [];
        
        this.executeLoad();
      },
      error: () => this.executeLoad(),
    });
  }

  private get isLocalOnly(): boolean {
    return !this.parentRecordId || this.hasUnsavedImportedRows;
  }

  private getFlattenedFormFields(): any[] {
    if (!this.formSavedFields) return [];
    
    const fields: any[] = [];
    
    if (this.formSavedFields.HeaderFields) {
      fields.push(...this.formSavedFields.HeaderFields);
    }
    
    if (this.formSavedFields.TabData) {
      for (const tab of this.formSavedFields.TabData) {
        if (tab.Columns) fields.push(...tab.Columns);
        if (tab.Footer) fields.push(...tab.Footer);
        if (tab.Sections) {
          for (const section of tab.Sections) {
            if (section.Columns) fields.push(...section.Columns);
          }
        }
      }
    }
    
    return fields;
  }

  private executeLoad(): void {
    if (this.hasUnsavedImportedRows) {
      return;
    }

    // After the initial config has been built, skip server calls when there
    // is no parent record (/add page). Everything stays client-side.
    if (!this.parentRecordId && this.config) {
      this.config.serverSidePagination = false;
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.dataService.loadRows({
      tableKey: this.tableKey,
      reportKey: this.reportKey,
      isEmbedded: true,
      referenceFieldKey: this.referenceFieldKey,
      lookupFieldValue: this.parentRecordId,
      start: this.currentPage * this.pageSize,
      length: this.pageSize,
      draw: ++this.draw,
      searchValue: this.searchValue,
      orderColumn: this.sortColumn,
      orderDir: this.sortDir,
      columns: this.payloadColumns,
    }).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res) => {
        const data = Array.isArray(res.Data) ? res.Data : [];
        this.rows = data;
        this.totalRecords = Number(res.RecordsTotal) || 0;

        // Build data key map from actual row keys (backend prefixes with tbl{id}_)
        const sampleRow = data[0] ?? {};
        this.dataKeyMap = {};
        this.columnKeyToFieldKey = {};
        this.columnKeyToFieldName = {};
        this.columnKeyToFieldType = {};
        for (const fullKey of Object.keys(sampleRow)) {
          const idx = fullKey.indexOf('_');
          if (idx > 0) {
            this.dataKeyMap[fullKey.substring(idx + 1)] = fullKey;
          }
          this.dataKeyMap[fullKey] = fullKey;
        }
        // Map data column keys -> field identifiers for CRUD
        for (const field of this.fields) {
          const dataKey = this.dataKeyMap[field.FieldName] ?? field.FieldName;
          this.columnKeyToFieldKey[dataKey] = field.Key;
          this.columnKeyToFieldName[dataKey] = field.FieldName;
          this.columnKeyToFieldType[dataKey] = field.FieldType;
        }

        // Build config from field metadata
        const columns = this.dataService.buildChildTableColumns(this.fields, this.dataKeyMap);

        const formFields = this.getFlattenedFormFields();

        for (const col of columns) {
          const field = this.fields.find(
            f => (this.dataKeyMap[f.FieldName] ?? f.FieldName) === col.key,
          );
          
          if (field) {
            if (this.userOptions.length > 0 && field.FieldType?.toUpperCase() === 'USER') {
              col.type = 'select' as any;
              (col as any).selectOptions = [
                { value: '', label: '-- Select --' },
                ...this.userOptions,
              ];
            }

            // Merge static form settings
            const formField = formFields.find(f => f.FieldKey === field.Key || f.FieldName === field.FieldName);
            if (formField?.FormFieldSetting) {
              const settings = formField.FormFieldSetting;
              if (settings.Required === 1) {
                (col as any)._staticRequired = true;
                col.validation = col.validation ?? [];
                if (!col.validation.some(v => v.type === 'required')) {
                  col.validation.push({ type: 'required', message: 'This field is required' });
                }
              }
              if (settings.Readonly === 1) {
                (col as any)._staticReadonly = true;
                col.editable = false;
              }
              if (settings.Hidden === 1) {
                (col as any)._staticHidden = true;
              }
            }
          }
        }

        this.config = {
          columns,
          idField: this.dataKeyMap['id'] ?? 'rid',
          selectable: true,
          selectionMode: 'multi',
          editable: this.editable,
          rowReorderable: false,
          sortable: true,
          filterable: true,
          serverSidePagination: !!this.parentRecordId,
          title: this.displayTitle,
          description: this.embeddedSetting?.InfoText ?? '',
          emptyMessage: 'No records found. Click "+" to add a row.',
          autoSaveDebounce: 500,
        };

        this.evaluateAllRules(data, columns);

        this.loading = false;
        this.updateValidationStatus();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        this.error = 'Failed to load child table data.';
        this.cdr.markForCheck();
      },
    });
  }

  // -------------------------------------------------------------------
  // Dynamic Form Rules
  // -------------------------------------------------------------------

  private evaluateAllRules(rows: any[], columns: ChildTableColumn<any>[]): void {
    if (!this.formRules || this.formRules.length === 0) return;

    this.rowFieldStates.clear();
    const idField = this.dataKeyMap['id'] ?? 'rid';

    // Create a reverse map: fieldKey -> columnKey
    const fieldKeyToColKey: Record<string, string> = {};
    for (const [colKey, fieldKey] of Object.entries(this.columnKeyToFieldKey)) {
      fieldKeyToColKey[fieldKey] = colKey;
    }

    for (const row of rows) {
      const rowId = String(row[idField] ?? row['rid'] ?? '');
      const states = evaluateRules(
        this.formRules,
        row,
        this.columnKeyToFieldKey,
        fieldKeyToColKey,
      );
      this.rowFieldStates.set(rowId, states);
    }

    this.applyAggregateColumnStates(columns);
  }

  private evaluateRowRules(rowId: string, rowData: any): void {
    if (!this.formRules || this.formRules.length === 0 || !this.config) return;

    const fieldKeyToColKey: Record<string, string> = {};
    for (const [colKey, fieldKey] of Object.entries(this.columnKeyToFieldKey)) {
      fieldKeyToColKey[fieldKey] = colKey;
    }

    const states = evaluateRules(
      this.formRules,
      rowData,
      this.columnKeyToFieldKey,
      fieldKeyToColKey,
    );

    this.rowFieldStates.set(rowId, states);

    for (const [fieldKey, state] of Object.entries(states)) {
      if (state.newValue !== undefined) {
        const colKey = fieldKeyToColKey[fieldKey];
        if (colKey && rowData[colKey] !== state.newValue) {
          this.childTableRef?.setCellValue(rowId, colKey, state.newValue);
        }
      }
    }

    this.applyAggregateColumnStates(this.config.columns);
  }

  private applyAggregateColumnStates(columns: ChildTableColumn<any>[]): void {
    if (this.rowFieldStates.size === 0) return;

    const allStates = Array.from(this.rowFieldStates.values());

    for (const col of columns) {
      const fieldKey = this.columnKeyToFieldKey[col.key];
      if (!fieldKey) continue;

      const allHidden = allStates.every((s) => s[fieldKey]?.hidden === true);
      const anyReadonly = allStates.some((s) => s[fieldKey]?.readonly === true);
      const anyRequired = allStates.some((s) => s[fieldKey]?.required === true);

      const isSystem = ChildTableEmbeddedComponent.SYSTEM_FIELDS.has(
        (this.columnKeyToFieldName[col.key] ?? '').toLowerCase()
      );
      
      const isStaticReadonly = (col as any)._staticReadonly;
      if (!isSystem) {
        col.editable = !(isStaticReadonly || anyReadonly);
      }

      const isStaticRequired = (col as any)._staticRequired;
      if (anyRequired || isStaticRequired) {
        col.validation = col.validation ?? [];
        if (!col.validation.some((v: any) => v.type === 'required')) {
          col.validation.push({ type: 'required', message: `This field is required` });
        }
      } else if (col.validation) {
        col.validation = col.validation.filter((v: any) => v.type !== 'required');
      }

      const isStaticHidden = (col as any)._staticHidden;
      if (allHidden || isStaticHidden) {
        col.editable = false;
        // In the future, we can add col.hidden = true here if the grid supports it
      }
    }
  }

  // -------------------------------------------------------------------
  // Event handlers (child table outputs -> backend calls)
  // -------------------------------------------------------------------

  onCellChange(event: { rowId: string; columnKey: string; value: any; oldValue: any }): void {
    const rid = Number(event.rowId);

    const row = this.childTableRef?.stateService.getState().rows.find(r => r.id === event.rowId);
    if (row) {
      this.evaluateRowRules(event.rowId, row.data);
    }

    this.retryBlockedRowsIfValid();

    if (!rid || rid <= 0) return; // Temp rows are handled by autosave

    if (row?.errors && Object.keys(row.errors).length > 0) {
      return;
    }

    const rawFieldName = this.columnKeyToFieldName[event.columnKey] ?? event.columnKey;
    const fieldName = ChildTableEmbeddedComponent.stripTablePrefix(rawFieldName);
    const fieldType = (this.columnKeyToFieldType[event.columnKey] ?? '').toUpperCase();

    const onError = (err: any): void => {
      this.childTableRef?.revertRow(event.rowId);
      const msg = err?.error?.message ?? err?.error?.error_msg?.[0] ?? 'The value could not be saved. It has been reverted.';
      this.childTableRef?.showSaveError([msg]);
    };

    if (fieldType === 'FILE') {
      const val = event.value;
      if (val && typeof val === 'object' && val.base64) {
        this.dataService.updateCell(this.tableKey, this.applicationKey, rid, [
          { Key: fieldName, Value: val.name ?? '', Base64EncodedFile: val.base64.includes(',') ? val.base64.split(',')[1] : val.base64 },
        ]).pipe(takeUntil(this.destroy$)).subscribe({ error: onError });
      } else if (val === '' || val == null) {
        this.dataService.updateCell(this.tableKey, this.applicationKey, rid, [
          { Key: fieldName, Value: '' },
        ]).pipe(takeUntil(this.destroy$)).subscribe({ error: onError });
      }
      return;
    }

    this.dataService.updateCell(this.tableKey, this.applicationKey, rid, [
      { Key: fieldName, Value: String(event.value ?? '') },
    ]).pipe(takeUntil(this.destroy$)).subscribe({ error: onError });

    this.updateValidationStatus();
  }

  /**
   * After a cell edit, check if all previously blocked rows are now error-free.
   * If so, re-mark them as dirty so the auto-save cycle picks them up.
   */
  private retryBlockedRowsIfValid(): void {
    if (this.validationBlockedRowIds.size === 0) return;

    const allRows = this.childTableRef?.stateService.getState().rows ?? [];
    const blockedRows = allRows.filter(r => this.validationBlockedRowIds.has(r.id));
    if (blockedRows.length === 0) {
      this.validationBlockedRowIds.clear();
      return;
    }

    // Key fix: re-validate to clear errors for fields that now have values
    this.validateRows(blockedRows);

    // Re-fetch rows after validateRows updated error state in the state service
    const freshRows = this.childTableRef?.stateService.getState().rows ?? [];
    const freshBlocked = freshRows.filter(r => this.validationBlockedRowIds.has(r.id));
    const stillHasErrors = freshBlocked.some(
      r => r.errors && Object.keys(r.errors).length > 0
    );

    if (!stillHasErrors) {
      const ids = [...this.validationBlockedRowIds];
      this.validationBlockedRowIds.clear();
      this.hasShownValidationAlert = false;
      this.childTableRef?.clearSaveError();
      this.childTableRef?.stateService.markRowsDirty(ids);
    }
  }

  onRowAdd(row: ChildTableRow<any>): void {
    // New rows are added locally first; they get synced via the save handler
    this.updateValidationStatus();
  }

  onRowDelete(ids: string[]): void {
    for (const id of ids) {
      this.validationBlockedRowIds.delete(id);
    }
    this.updateValidationStatus();
    
    const numericIds = ids.map(Number).filter(n => n > 0);
    if (numericIds.length === 0) return;

    this.dataService.deleteRows(this.tableKey, numericIds).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: () => {
        this.totalRecords = Math.max(0, this.totalRecords - numericIds.length);
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = 'Failed to delete row(s).';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Public API: returns true if any row in the child table has validation errors
   * (e.g. required fields not filled). Called by the parent form to gate submission.
   */
  hasValidationErrors(): boolean {
    const allRows = this.childTableRef?.stateService.getState().rows ?? [];
    if (allRows.length === 0) return false;
    return this.validateRows(allRows);
  }

  private updateValidationStatus(): void {
    const hasErrors = this.hasValidationErrors();
    if (hasErrors !== this.currentValidationStatus) {
      this.currentValidationStatus = hasErrors;
      this.validationStatusChange.emit(hasErrors);
    }
  }

  private validateRows(rows: ChildTableRow<any>[]): boolean {
    if (!this.config) return false;
    let hasErrors = false;
    for (const row of rows) {
      for (const col of this.config.columns) {
        if (col.validation && col.validation.length > 0) {
          const val = row.data[col.key];
          const error = this.childTableRef?.stateService.validateCell(row.id, col.key, val);
          if (error) {
            this.childTableRef?.stateService.setCellError(row.id, col.key, error);
            hasErrors = true;
          } else {
            this.childTableRef?.stateService.setCellError(row.id, col.key, null);
          }
        }
      }
    }
    return hasErrors;
  }

  onSave(dirtyRows: ChildTableRow<any>[]): void {
    if (dirtyRows.length === 0) return;

    // Perform full validation pass on all dirty rows before saving
    const hasValidationErrors = this.validateRows(dirtyRows);

    // Re-fetch dirty rows since we might have just added errors to them
    const updatedDirtyRows = this.childTableRef?.stateService.getDirtyRows() || dirtyRows;
    const rowsWithErrors = updatedDirtyRows.filter(r => r.errors && Object.keys(r.errors).length > 0);
    
    if (rowsWithErrors.length > 0 || hasValidationErrors) {
      if (!this.hasShownValidationAlert) {
        this.childTableRef?.showSaveError([
          'Please fill in all required fields before saving.',
        ]);
        this.hasShownValidationAlert = true;
      }
      
      // Also check ALL rows (not just dirty ones) for validation errors
      const allRows = this.childTableRef?.stateService.getState().rows ?? [];
      const allRowsWithErrors = allRows.filter(r => r.errors && Object.keys(r.errors).length > 0);
      
      for (const row of allRowsWithErrors) {
        this.validationBlockedRowIds.add(row.id);
      }
      for (const row of dirtyRows) {
        this.validationBlockedRowIds.add(row.id);
      }
      
      this.updateValidationStatus();
      return;
    }
    
    this.hasShownValidationAlert = false;
    this.validationBlockedRowIds.clear();
    this.updateValidationStatus();

    const isRowNew = (row: ChildTableRow<any>): boolean => {
      const rid = Number(row.id);
      return !row.id || row.id.startsWith('temp_') || row.id.startsWith('row_') || !Number.isFinite(rid) || rid <= 0;
    };

    // On /add page, parent record doesn't exist yet -- track rows for re-sync later.
    // The info banner already tells the user to submit the form first.
    const hasNewRows = dirtyRows.some(isRowNew);
    if (hasNewRows && !this.parentRecordId) {
      for (const row of dirtyRows) {
        if (isRowNew(row)) {
          this.pendingRowIds.add(row.id);
        }
      }
      return;
    }

    if (this.syncInFlight) {
      // Queue rows for the next sync cycle instead of dropping them
      this.syncQueue.push(...dirtyRows);
      return;
    }

    const isSystemField = (fieldName: string): boolean =>
      ChildTableEmbeddedComponent.SYSTEM_FIELDS.has(fieldName.toLowerCase());

    const items: SyncItem[] = dirtyRows.map(row => {
      const isNew = isRowNew(row);

      const fields = Object.entries(row.data as Record<string, any>)
        .filter(([key]) => key !== 'rid' && key !== 'id' && key !== 'tbl_id')
        .filter(([key]) => this.columnKeyToFieldName[key])
        .filter(([key]) => !isSystemField(this.columnKeyToFieldName[key]))
        .filter(([, val]) => isNew || (val !== undefined && val !== null))
        .filter(([key, val]) => {
          const ft = (this.columnKeyToFieldType[key] ?? '').toUpperCase();
          if (ft === 'FILE' && typeof val === 'string' && val !== '') return false;
          return true;
        })
        .map(([key, val]) => {
          const ft = (this.columnKeyToFieldType[key] ?? '').toUpperCase();
          const isFileUpload = ft === 'FILE' && val && typeof val === 'object' && val.base64;
          return {
            Name: ChildTableEmbeddedComponent.stripTablePrefix(this.columnKeyToFieldName[key]),
            Value: isFileUpload ? (val.name ?? '') : String(val ?? ''),
            ...(isFileUpload ? { Base64EncodedFile: (val.base64 as string).includes(',') ? (val.base64 as string).split(',')[1] : val.base64 as string } : {}),
          };
        });

      // Link new rows to the parent record via the reference field
      if (isNew && this.referenceFieldName && this.parentRecordId) {
        const fkName = ChildTableEmbeddedComponent.stripTablePrefix(this.referenceFieldName);
        const existingIdx = fields.findIndex(f => f.Name === fkName);
        if (existingIdx > -1) {
          fields.splice(existingIdx, 1);
        }
        fields.push({ Name: fkName, Value: String(this.parentRecordId) });
      }

      console.log('[ChildTable] Sync Item Fields:', fields);

      return {
        ApplicationTableKey: this.tableKey,
        ActionType: isNew ? 'new' as const : 'update' as const,
        ChildTempId: isNew ? row.id : undefined,
        Where: isNew ? undefined : { Rid: Number(row.id) },
        FieldsList: fields,
        LogTransaction: true,
      };
    });

    // Batch requests to avoid backend limits/timeouts with large datasets
    const BATCH_SIZE = 50;
    const batches: SyncItem[][] = [];
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      batches.push(items.slice(i, i + BATCH_SIZE));
    }

    this.syncInFlight = true;

    const showSyncProgress = this.hasUnsavedImportedRows && batches.length > 1;
    if (showSyncProgress) {
      this.childTableRef?.setImportProgress('saving', 0, batches.length);
    }

    // Execute batches sequentially
    const executeBatch = (index: number): void => {
      if (index >= batches.length) {
        // All batches done
        this.syncInFlight = false;
        this.error = null;
        this.childTableRef?.clearSaveError();
        if (showSyncProgress) this.childTableRef?.clearImportProgress();
        this.cdr.markForCheck();
        this.drainSyncQueue();
        return;
      }

      if (showSyncProgress && this.childTableRef?.importProgress?.cancelled) {
        this.syncInFlight = false;
        this.childTableRef?.clearImportProgress();
        this.cdr.markForCheck();
        return;
      }

      this.dataService.syncChanges(batches[index]).subscribe({
        next: (response) => {
          if (response?.added_recs && Object.keys(response.added_recs).length > 0) {
            this.childTableRef?.applySyncResult(response.added_recs);
          }
          
          if (this.hasUnsavedImportedRows && !response?.error_msg?.length) {
            // Only restore server-side pagination if this is the LAST batch
            if (index === batches.length - 1) {
              this.hasUnsavedImportedRows = false;
              if (this.config) {
                this.config.serverSidePagination = true;
              }
            }
          }

          if (showSyncProgress) {
            this.childTableRef?.updateImportProgress(index + 1);
          }

          if (response?.error_msg?.length) {
            for (const errMsg of response.error_msg) {
              const ridMatch = /Record #(\d+)/.exec(errMsg);
              if (ridMatch) {
                this.childTableRef?.revertRow(ridMatch[1]);
              }
            }
            this.childTableRef?.showSaveError(response.error_msg);
            this.syncInFlight = false;
            if (showSyncProgress) this.childTableRef?.clearImportProgress();
            return;
          }
          
          // Next batch
          executeBatch(index + 1);
        },
        error: () => {
          this.syncInFlight = false;
          if (showSyncProgress) this.childTableRef?.clearImportProgress();
          const batchRowIds = batches[index]
            .map(item => item.ChildTempId ?? (item.Where ? String(item.Where.Rid) : null))
            .filter((id): id is string => id !== null);
            
          for (const rowId of batchRowIds) {
            this.childTableRef?.revertRow(rowId);
          }
          this.childTableRef?.showSaveError(['Failed to save changes. Your edits have been reverted. Please try again.']);
          this.cdr.markForCheck();
        },
      });
    };

    executeBatch(0);
  }

  onPageChange(pageIndex: number): void {
    this.currentPage = pageIndex;
    if (!this.isLocalOnly) this.executeLoad();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 0;
    if (!this.isLocalOnly) this.executeLoad();
  }

  onSortChange(sort: any): void {
    if (sort) {
      const colIndex = this.config?.columns.findIndex(c => c.key === sort.column) ?? -1;
      this.sortColumn = colIndex;
      this.sortDir = sort.direction ?? 'asc';
    } else {
      this.sortColumn = -1;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
    if (!this.isLocalOnly) this.executeLoad();
  }

  onSearchChange(searchTerm: string): void {
    this.searchValue = searchTerm;
    this.currentPage = 0;
    if (!this.isLocalOnly) this.executeLoad();
  }

  onImportComplete(result: ImportResult<any>): void {
    if (result.success) {
      // Local import (from file input in toolbar - legacy path)
      // OR Server-side import (from ImportModalComponent) that returns data directly
      if (result.data && result.data.length > 0) {
        // Don't reload from server or update [data] binding directly, as that would overwrite
        // the temp IDs assigned by ChildTableComponent.addRow().
        this.hasUnsavedImportedRows = true;

        // Switch to client-side pagination so the user can paginate/sort/search locally.
        // IMPORTANT: We mutate config.serverSidePagination in-place rather than creating
        // a new config object. Creating a new object would trigger ngOnChanges in
        // ChildTableComponent, which re-initializes the state via createRows(), destroying
        // the temp IDs (row_xxx) assigned by addRow() and replacing them with index-based
        // IDs ("0", "1", ...). That breaks syncPendingAndWait's isNew() check, causing
        // only row "0" to be treated as new and the other 99 to be sent as updates with
        // wrong Rids.
        if (this.config) {
          this.config.serverSidePagination = false;
        }

        // Update total records from the actual state (which includes the new rows)
        const currentRows = this.childTableRef?.stateService.getState().rows ?? [];
        this.totalRecords = currentRows.length;
        this.cdr.markForCheck();

        // When the parent record already exists, persist immediately instead of
        // relying on debounced autosave. This avoids the scenario where a single
        // cell edit later triggers syncing thousands of imported rows.
        if (this.parentRecordId) {
          const newRows = currentRows.filter(r =>
            r.id.startsWith('temp_') || r.id.startsWith('row_') || !Number.isFinite(Number(r.id)) || Number(r.id) <= 0
          );
          if (newRows.length > 0) {
            this.onSave(newRows);
          }
        }
      } else {
        // Server-side import (from ImportModalComponent) where data was saved to DB
        // Data is already on the server, so we should reload.
        this.hasUnsavedImportedRows = false; // Allow reload
        this.currentPage = 0; // Reset to first page to see new data
        this.executeLoad();
      }
    }
  }

  /**
   * Deduplicate and flush the sync queue. Called after a sync cycle finishes.
   * Keeps only the latest version of each row (by ID) to avoid sending stale data.
   */
  private drainSyncQueue(): void {
    if (this.syncQueue.length === 0) return;

    const deduped = new Map<string, ChildTableRow<any>>();
    for (const row of this.syncQueue) {
      deduped.set(row.id, row);
    }
    this.syncQueue = [];

    // Re-read the latest state for each queued row so we send the most current values
    const allRows = this.childTableRef?.stateService.getState().rows ?? [];
    const freshRows = [...deduped.keys()]
      .map(id => allRows.find(r => r.id === id))
      .filter((r): r is ChildTableRow<any> => !!r);

    if (freshRows.length > 0) {
      this.onSave(freshRows);
    }
  }

  /**
   * Sync pending rows immediately now that parentRecordId is available.
   * Bypasses the 500ms debounce to prevent data loss on quick navigation
   * (e.g. Save & Exit destroying the component before debounce fires).
   */
  private resyncPendingRows(): void {
    const ids = new Set(this.pendingRowIds);
    this.pendingRowIds.clear();
    this.error = null;

    const allRows = this.childTableRef?.stateService.getState().rows ?? [];
    const pendingRows = allRows.filter(r => ids.has(r.id));
    if (pendingRows.length > 0) {
      this.onSave(pendingRows);
    }
    this.cdr.markForCheck();
  }

  /**
   * Public API: sync all pending child rows and return a Promise that resolves
   * when the HTTP request completes. Called by the parent form before navigating away.
   *
   * Handles two scenarios:
   *   1. ngOnChanges already fired (via detectChanges) and started a sync -- just wait for it.
   *   2. No sync started yet -- find all new rows and trigger the sync ourselves.
   */
  syncPendingAndWait(parentId: string): Promise<void> {
    this.parentRecordId = parentId;
    this.error = null;

    const waitForSync = (): Promise<void> => new Promise(resolve => {
      const check = setInterval(() => {
        if (!this.syncInFlight) { clearInterval(check); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(check); resolve(); }, 10000);
    });

    // If resyncPendingRows (via ngOnChanges) already kicked off a sync, just wait for it
    if (this.syncInFlight) {
      return waitForSync();
    }

    // Otherwise find all unsaved (new) rows and sync them ourselves
    const allRows = this.childTableRef?.stateService.getState().rows ?? [];
    const isNew = (r: ChildTableRow<any>): boolean => {
      const rid = Number(r.id);
      return !r.id || r.id.startsWith('temp_') || r.id.startsWith('row_') || !Number.isFinite(rid) || rid <= 0;
    };
    const newRows = allRows.filter(isNew);
    this.pendingRowIds.clear();

    if (newRows.length === 0) {
      return Promise.resolve();
    }

    this.onSave(newRows);
    return waitForSync().then(() => {
      // After sync completes, we intentionally do NOT reset hasUnsavedImportedRows here.
      //
      // Reason: The parent form often triggers a full reload (LoadFormData) immediately after save.
      // If we reset the flag now, that reload would call executeLoad(), which would fetch from
      // the backend. Since the backend transaction might not be fully committed or replicated yet,
      // the fetch could return 0 rows, wiping out the table.
      //
      // By keeping hasUnsavedImportedRows = true, we block executeLoad() from running during the
      // parent form's reload cycle. The local rows (which now have real IDs via applySyncResult)
      // remain visible and correct.
      //
      // The flag will naturally be cleared when the user navigates away and back (component destroy/init),
      // or if they manually refresh the page, at which point the backend data should be ready.
    });
  }
}
